// =====================================================
// CLINICAL RECORDS PAGE - Página Principal de Historias Clínicas
// Lista de registros agrupados por PACIENTE con fechas Argentina
// =====================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Plus,
  Filter,
  FileText,
  FileSpreadsheet,
  Calendar,
  User,
  ChevronRight,
  ChevronDown,
  Clock,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  Loader2,
  Eye,
  Edit3,
  Trash2,
  MoreVertical,
  X,
  ClipboardList,
  FolderOpen,
  Share2,
  UserCheck,
  Link2,
  Settings2,
  // Íconos para plantillas
  Stethoscope,
  Baby,
  Heart,
  Shield,
  Users,
  Activity,
  Pill,
  Syringe,
  Thermometer,
  Brain,
  Bone,
  HeartPulse,
  type LucideIcon
} from 'lucide-react';

import {
  ClinicalRecord,
  ClinicalTemplate,
  PatientSearchResult
} from '../../types/clinicalRecords.types';

import clinicalRecordsApi, {
  getRecordAgeBadge
} from '../../services/clinicalRecords.api';

import ExportDateRangeModal from '../ExportDateRangeModal';

// =====================================================
// HELPER PARA FECHAS EN ZONA HORARIA ARGENTINA
// =====================================================

const formatDateArgentina = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/Argentina/Buenos_Aires'
  });
};

const formatDateTimeArgentina = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Argentina/Buenos_Aires'
  });
};

const formatDateShort = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'America/Argentina/Buenos_Aires'
  });
};

// =====================================================
// HELPER PARA ÍCONOS DINÁMICOS
// =====================================================

const iconMap: Record<string, LucideIcon> = {
  'stethoscope': Stethoscope,
  'clipboard': ClipboardList,
  'clipboard-list': ClipboardList,
  'baby': Baby,
  'heart': Heart,
  'shield': Shield,
  'users': Users,
  'child': Users,
  'activity': Activity,
  'pill': Pill,
  'syringe': Syringe,
  'thermometer': Thermometer,
  'eye': Eye,
  'brain': Brain,
  'bone': Bone,
  'heart-pulse': HeartPulse,
  'file-text': FileText
};

interface DynamicIconProps {
  name: string;
  className?: string;
}

const DynamicIcon: React.FC<DynamicIconProps> = ({ name, className = "w-6 h-6" }) => {
  const IconComponent = iconMap[name?.toLowerCase()];
  
  if (IconComponent) {
    return <IconComponent className={className} />;
  }
  
  // Fallback: mostrar ícono por defecto
  return <ClipboardList className={className} />;
};

// =====================================================
// INTERFACES
// =====================================================

interface Filters {
  search: string;
  templateId: number | null;
  dateFrom: string;
  dateTo: string;
}

interface PatientGroup {
  patientKey: string;
  patientName: string;
  patientDni?: string;
  patientEmail?: string;
  records: ClinicalRecord[];
  lastVisit: string;
  totalRecords: number;
}

// ✅ Interface para el paciente preseleccionado desde Mis Pacientes
interface PreselectedPatient {
  name: string;
  email?: string;
  phone?: string;
}

// =====================================================
// COMPONENTE DE TARJETA DE PACIENTE (AGRUPADO)
// =====================================================

interface PatientCardProps {
  group: PatientGroup;
  templates: ClinicalTemplate[];
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (record: ClinicalRecord) => void;
  onNewRecord: (patientName: string, patientDni?: string, patientEmail?: string, patientPhone?: string) => void;
}

const PatientCard: React.FC<PatientCardProps> = ({ 
  group, 
  templates, 
  onView, 
  onEdit, 
  onDelete,
  onNewRecord 
}) => {
  const [isExpanded, setIsExpanded] = useState(group.records.length === 1);

  const getTemplateInfo = (templateId: number) => {
    return templates.find(t => t.id === templateId);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header del paciente */}
      <div 
        className="p-4 sm:p-5 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Avatar del paciente */}
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-white" />
            </div>
            
            {/* Info del paciente */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {group.patientName}
                </h3>
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                  {group.totalRecords} {group.totalRecords === 1 ? 'ficha' : 'fichas'}
                </span>
              </div>
              
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                {group.patientDni && (
                  <span>DNI: {group.patientDni}</span>
                )}
                {group.patientEmail && (
                  <span className="hidden sm:inline">• {group.patientEmail}</span>
                )}
              </div>
              
              <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                <Clock className="w-3 h-3" />
                Última consulta: {formatDateShort(group.lastVisit)}
              </div>
            </div>
          </div>
          
          {/* Botón expandir */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNewRecord(group.patientName, group.patientDni, group.patientEmail, group.records[0]?.patient_phone);
              }}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Nueva ficha para este paciente"
            >
              <Plus className="w-5 h-5" />
            </button>
            <div className={`p-2 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
              <ChevronDown className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Lista de fichas del paciente */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {group.records.map((record, index) => {
            const template = getTemplateInfo(record.template_id);
            const ageBadge = getRecordAgeBadge(record.created_at);
            
            return (
              <div
                key={record.id}
                className={`p-4 sm:pl-20 hover:bg-gray-50 transition-colors ${
                  index < group.records.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Toda la info es clickeable: abre la historia (como el ojito) */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => onView(record.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onView(record.id); } }}
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer rounded-lg -m-1 p-1 hover:bg-blue-50/40 transition-colors"
                    title="Ver historia clínica"
                  >
                    {/* Icono de plantilla */}
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${template?.color || '#3B82F6'}15`, color: template?.color || '#3B82F6' }}
                    >
                      <DynamicIcon name={template?.icon || 'clipboard-list'} className="w-5 h-5" />
                    </div>
                    
                    {/* Info de la ficha */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">
                          {record.template_name || template?.name || 'Historia Clínica'}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${ageBadge.color}`}>
                          {ageBadge.label}
                        </span>
                        {record.version && record.version > 1 && (
                          <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                            v{record.version}
                          </span>
                        )}
                        {(record as any).is_owner === false && (
                          <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                            <Share2 className="w-3 h-3" />
                            {(record as any).owner_name || 'Compartida'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>Consulta: {formatDateShort(record.record_date)}</span>
                        <span className="text-gray-300">•</span>
                        <span>Creado: {formatDateTimeArgentina(record.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Acciones */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onView(record.id)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Ver"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onEdit(record.id)}
                      className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(record)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const ClinicalRecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // ✅ Obtener paciente preseleccionado del state de navegación (desde Mis Pacientes)
  const preselectedPatient = location.state?.preselectedPatient as PreselectedPatient | undefined;
  
  // Estados
  const [records, setRecords] = useState<ClinicalRecord[]>([]);
  const [templates, setTemplates] = useState<ClinicalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    templateId: null,
    dateFrom: '',
    dateTo: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<ClinicalRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<'patients' | 'chronological'>('patients');
  const [ownershipFilter, setOwnershipFilter] = useState<'all' | 'mine' | 'shared'>('all');
  const [showExportModal, setShowExportModal] = useState(false);
  
  // ✅ Efecto para manejar paciente preseleccionado
  useEffect(() => {
    if (preselectedPatient?.name) {
      // Navegar directamente a crear nueva ficha con el paciente preseleccionado
      const params = new URLSearchParams();
      params.set('patient', preselectedPatient.name);
      if (preselectedPatient.email) params.set('email', preselectedPatient.email);
      if (preselectedPatient.phone) params.set('phone', preselectedPatient.phone);
      
      // Limpiar el state para evitar loops
      navigate(`/professional/clinical-records/new?${params.toString()}`, { 
        replace: true,
        state: {} // Limpiar state
      });
    }
  }, [preselectedPatient, navigate]);
  
  // Cargar datos iniciales
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Cargar plantillas y registros en paralelo
      const [templatesRes, recordsRes] = await Promise.all([
        clinicalRecordsApi.getTemplates(),
        clinicalRecordsApi.getRecords({
          patientName: filters.search || undefined,
          templateId: filters.templateId || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          limit: 100,
          filter: ownershipFilter
        } as any)
      ]);
      
      setTemplates(templatesRes.templates || []);
      setRecords(recordsRes.records || []);
    } catch (err: any) {
      console.error('Error cargando datos:', err);
      setError(err.message || 'Error al cargar historias clínicas');
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.templateId, filters.dateFrom, filters.dateTo, ownershipFilter]);
  
  useEffect(() => {
    // Solo cargar datos si no hay paciente preseleccionado (evitar carga innecesaria)
    if (!preselectedPatient?.name) {
      loadData();
    }
  }, [loadData, preselectedPatient]);
  
  // Handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
  };
  
  const handleFilterChange = (key: keyof Filters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const clearFilters = () => {
    setFilters({
      search: '',
      templateId: null,
      dateFrom: '',
      dateTo: ''
    });
  };
  
  const handleNewRecord = () => {
    navigate('/professional/clinical-records/new');
  };

  // --- Exportar Historias Clínicas a Excel (via Backend) ---
  const exportRecordsToExcel = async (fromDate: string, toDate: string) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_URL}/api/clinical-records/export/excel?from=${fromDate}&to=${toDate}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Error al exportar historias clínicas');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historias_clinicas_${fromDate}_${toDate}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleNewRecordForPatient = (patientName: string, patientDni?: string, patientEmail?: string, patientPhone?: string) => {
    const params = new URLSearchParams();
    if (patientName) params.set('patient', patientName);
    if (patientDni) params.set('dni', patientDni);
    if (patientEmail) params.set('email', patientEmail);
    if (patientPhone) params.set('phone', patientPhone);
    navigate(`/professional/clinical-records/new?${params.toString()}`);
  };
  
  const handleViewRecord = (recordId: number) => {
    navigate(`/professional/clinical-records/${recordId}`);
  };
  
  const handleEditRecord = (recordId: number) => {
    navigate(`/professional/clinical-records/${recordId}/edit`);
  };
  
  const handleDeleteClick = (record: ClinicalRecord) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    if (!recordToDelete) return;
    
    setDeleting(true);
    try {
      await clinicalRecordsApi.deleteRecord(recordToDelete.id);
      setRecords(prev => prev.filter(r => r.id !== recordToDelete.id));
      setShowDeleteModal(false);
      setRecordToDelete(null);
    } catch (err: any) {
      alert('Error al eliminar: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };
  
  const getTemplateInfo = (templateId: number) => {
    return templates.find(t => t.id === templateId);
  };
  
  // Agrupar registros por paciente
  const groupedByPatient: PatientGroup[] = React.useMemo(() => {
    const groups: Record<string, PatientGroup> = {};
    
    records.forEach(record => {
      // Usar DNI como clave principal, o email, o nombre
      const patientKey = record.patient_dni || record.patient_email || record.patient_name || 'sin-identificar';
      
      if (!groups[patientKey]) {
        groups[patientKey] = {
          patientKey,
          patientName: record.patient_name || 'Paciente sin nombre',
          patientDni: record.patient_dni,
          patientEmail: record.patient_email,
          records: [],
          lastVisit: record.record_date || record.created_at,
          totalRecords: 0
        };
      }
      
      groups[patientKey].records.push(record);
      groups[patientKey].totalRecords++;
      
      // Actualizar última visita si es más reciente
      const currentLast = new Date(groups[patientKey].lastVisit);
      const recordDate = new Date(record.record_date || record.created_at);
      if (recordDate > currentLast) {
        groups[patientKey].lastVisit = record.record_date || record.created_at;
      }
    });
    
    // Ordenar registros dentro de cada grupo por fecha (más reciente primero)
    Object.values(groups).forEach(group => {
      group.records.sort((a, b) => {
        const dateA = new Date(a.record_date || a.created_at);
        const dateB = new Date(b.record_date || b.created_at);
        return dateB.getTime() - dateA.getTime();
      });
    });
    
    // Convertir a array y ordenar por última visita (más reciente primero)
    return Object.values(groups).sort((a, b) => {
      return new Date(b.lastVisit).getTime() - new Date(a.lastVisit).getTime();
    });
  }, [records]);

  // Agrupar registros por fecha (modo cronológico)
  const groupedByDate = React.useMemo(() => {
    const groups: Record<string, ClinicalRecord[]> = {};
    
    records.forEach(record => {
      const date = record.record_date?.split('T')[0] || 'sin-fecha';
      if (!groups[date]) groups[date] = [];
      groups[date].push(record);
    });
    
    return groups;
  }, [records]);
  
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => 
    new Date(b).getTime() - new Date(a).getTime()
  );

  // =====================================================
  // RENDER
  // =====================================================
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between min-h-[56px] sm:h-16 py-2 sm:py-0">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                onClick={() => navigate('/dashboard/profesional')}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="hidden sm:flex w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center flex-shrink-0">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">Historias Clínicas</h1>
                <p className="text-xs text-gray-500">
                  {groupedByPatient.length} paciente{groupedByPatient.length !== 1 ? 's' : ''} • {records.length} ficha{records.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-1.5 border border-green-200 bg-green-50 text-green-700 p-2 sm:px-3 sm:py-2.5 rounded-xl font-medium hover:bg-green-100 transition-all active:scale-95"
                title="Exportar historias clínicas a Excel"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Exportar</span>
              </button>
              <button
                onClick={() => navigate('/professional/clinical-records/template/new')}
                className="flex items-center gap-1.5 border border-gray-200 text-gray-700 p-2 sm:px-3 sm:py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-all active:scale-95"
                title="Crear plantilla"
              >
                <Settings2 className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Crear Plantilla</span>
              </button>
              <button
                onClick={handleNewRecord}
                className="flex items-center gap-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline text-sm">Nueva Historia</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Barra de búsqueda y filtros */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Campo de búsqueda */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o DNI del paciente..."
                value={filters.search}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
            
            {/* Botones de acción */}
            <div className="flex items-center gap-2">
              {/* Toggle de vista */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('patients')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'patients' 
                      ? 'bg-white shadow text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Users className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('chronological')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'chronological' 
                      ? 'bg-white shadow text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                </button>
              </div>
              
              {/* Botón de filtros */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl transition-all ${
                  showFilters || filters.templateId || filters.dateFrom || filters.dateTo
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-5 h-5" />
                <span className="hidden sm:inline">Filtros</span>
              </button>
              
              {/* Botón refrescar */}
              <button
                onClick={loadData}
                disabled={loading}
                className="p-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          
          {/* Panel de filtros expandido */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              {/* Tabs de propiedad */}
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => setOwnershipFilter('all')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    ownershipFilter === 'all'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  <FolderOpen className="w-4 h-4" />
                  Todas
                </button>
                <button
                  onClick={() => setOwnershipFilter('mine')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    ownershipFilter === 'mine'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  Mis Fichas
                </button>
                <button
                  onClick={() => setOwnershipFilter('shared')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    ownershipFilter === 'shared'
                      ? 'bg-purple-100 text-purple-700'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  <Share2 className="w-4 h-4" />
                  Compartidas conmigo
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Filtro por plantilla */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Tipo de Historia
                  </label>
                  <select
                    value={filters.templateId || ''}
                    onChange={(e) => handleFilterChange('templateId', e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Todas las plantillas</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Filtro fecha desde */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Desde
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {/* Filtro fecha hasta */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Hasta
                  </label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Botón limpiar filtros */}
              {(filters.templateId || filters.dateFrom || filters.dateTo) && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                  >
                    <X className="w-4 h-4" />
                    Limpiar filtros
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* Estado de carga */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
            <p className="text-gray-500">Cargando historias clínicas...</p>
          </div>
        )}
        
        {/* Error */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">Error al cargar</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Reintentar
            </button>
          </div>
        )}
        
        {/* Estado vacío */}
        {!loading && !error && records.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-blue-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {filters.search || filters.templateId || filters.dateFrom || filters.dateTo
                ? 'No se encontraron resultados'
                : 'Aún no hay historias clínicas'
              }
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {filters.search || filters.templateId || filters.dateFrom || filters.dateTo
                ? 'Intenta ajustar los filtros de búsqueda'
                : 'Comienza creando la primera historia clínica de un paciente'
              }
            </p>
            {!filters.search && !filters.templateId && !filters.dateFrom && !filters.dateTo && (
              <button
                onClick={handleNewRecord}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all"
              >
                <Plus className="w-5 h-5" />
                Crear Primera Historia
              </button>
            )}
          </div>
        )}
        
        {/* VISTA POR PACIENTES */}
        {!loading && !error && records.length > 0 && viewMode === 'patients' && (
          <div className="space-y-4">
            {groupedByPatient.map(group => (
              <PatientCard
                key={group.patientKey}
                group={group}
                templates={templates}
                onView={handleViewRecord}
                onEdit={handleEditRecord}
                onDelete={handleDeleteClick}
                onNewRecord={handleNewRecordForPatient}
              />
            ))}
          </div>
        )}

        {/* VISTA CRONOLÓGICA */}
        {!loading && !error && records.length > 0 && viewMode === 'chronological' && (
          <div className="space-y-6">
            {sortedDates.map(date => (
              <div key={date}>
                {/* Encabezado de fecha */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg shadow-sm border border-gray-100">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">
                      {formatDateArgentina(date)}
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-sm text-gray-400">
                    {groupedByDate[date].length} registro{groupedByDate[date].length !== 1 ? 's' : ''}
                  </span>
                </div>
                
                {/* Cards de registros */}
                <div className="space-y-3">
                  {groupedByDate[date].map((record: ClinicalRecord) => {
                    const template = getTemplateInfo(record.template_id);
                    const ageBadge = getRecordAgeBadge(record.created_at);
                    
                    return (
                      <div
                        key={record.id}
                        className="bg-white rounded-xl shadow-sm border border-gray-100 transition-all hover:shadow-md"
                      >
                        <div className="p-4 sm:p-5">
                          <div className="flex items-start justify-between gap-4">
                            {/* Info principal */}
                            <div className="flex items-start gap-4 flex-1 min-w-0">
                              {/* Icono de plantilla */}
                              <div
                                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${template?.color || '#3B82F6'}15`, color: template?.color || '#3B82F6' }}
                              >
                                <DynamicIcon name={template?.icon || 'clipboard-list'} className="w-6 h-6" />
                              </div>
                              
                              {/* Datos del registro */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                                    {record.patient_name}
                                  </h3>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${ageBadge.color}`}>
                                    {ageBadge.label}
                                  </span>
                                </div>
                                
                                <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <FileText className="w-3.5 h-3.5" />
                                    {record.template_name || template?.name || 'Sin plantilla'}
                                  </span>
                                  {record.patient_dni && (
                                    <span className="flex items-center gap-1">
                                      <User className="w-3.5 h-3.5" />
                                      DNI: {record.patient_dni}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Acciones */}
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleViewRecord(record.id)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Ver historia"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleEditRecord(record.id)}
                                className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                title="Editar"
                              >
                                <Edit3 className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(record)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Footer con metadata */}
                          <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              Creado: {formatDateTimeArgentina(record.created_at)}
                            </div>
                            {record.version && record.version > 1 && (
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-500">
                                v{record.version}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && recordToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  Eliminar Historia Clínica
                </h3>
                <p className="mt-2 text-gray-600">
                  ¿Estás seguro de eliminar la historia clínica de <strong>{recordToDelete.patient_name}</strong>?
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setRecordToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exportar Historias Clínicas */}
      <ExportDateRangeModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={exportRecordsToExcel}
        title="Exportar Historias Clínicas"
        description="Selecciona el rango de fechas para exportar"
      />
    </div>
  );
};

export default ClinicalRecordsPage;